let filteredCollectionTypes = new Set();
let matchedProductType = null;
let matchedOemPartNumber = null;

/**
 * Vehicle-Scoped Predictive Search
 * Integrates with EasySearch to filter product results by fitment
 * Based on requirements from predictive_searchbar.md
 */

function createPredictiveSearch() {
	// DOM elements
	const form = document.querySelector("[data-predictive-search-form]");
	const input = document.querySelector("[data-predictive-search-input]");
	const resultsContainer = document.querySelector(
		"[data-predictive-search-results]",
	);
	const closeButton = document.querySelector("[data-search-close]");

	// State
	let debounceTimer = null;
	let currentQuery = "";
	let isOpen = false;
	let cachedSelectedVehicle = null;
	let cachedCollectionHandle = null; // Cached collection handle to avoid repeated conversions
	let fuseInstance = null; // Reusable Fuse.js instance
	const universalProductsByCollectionHandle = new Map();

	const STOREFRONT_API_VERSION = "2025-10";
	const STOREFRONT_ACCESS_TOKEN = "44a939c332cecf3e7d07afc3cdc65b09";
	const OEM_PART_NUMBER_METAFIELD = {
		namespace: "custom",
		key: "product_oem_part_numbers",
	};

	// Promise used to delay form submission until input handling completes
	let pendingInputPromise = null;
	let pendingInputResolve = null;
	let pendingInputReject = null;

	// Incrementing request id to track latest search and avoid stale responses
	let lastRequestId = 0;

	// Track matching products count for logging
	let lastMatchingProductsCount = 0;

	// Early return if elements not found
	if (!form || !input || !resultsContainer) {
		console.warn("Predictive search elements not found");
		return null;
	}

	/**
	 * Get the selected vehicle from EasySearch localStorage
	 * Returns the YMM string or null
	 */
	function getSelectedVehicleYMM(all = true, ind = false) {
		if (all) {
			try {
				const raw = localStorage.getItem("searchTerms");
				if (!raw) return null;

				const parsed = JSON.parse(raw);
				if (!Array.isArray(parsed) || !parsed.length) return null;

				return parsed[0].ymm?.trim() || null;
			} catch (error) {
				console.error("Error reading vehicle from localStorage:", error);
				return null;
			}
		} else if (ind) {
			try {
				const raw = localStorage.getItem("searchTerms");
				if (!raw) return null;

				const parsed = JSON.parse(raw);
				if (!Array.isArray(parsed) || !parsed.length) return null;

				return {
					year: parsed[0].year?.trim() || null,
					make: parsed[0].make?.trim() || null,
					model: parsed[0].model?.trim() || null,
					submodel: parsed[0].submodel?.trim() || null,
					engine: parsed[0].engine?.trim() || null,
				};
			} catch (error) {
				console.error(
					"Error reading individual vehicle data from localStorage:",
					error,
				);
				return null;
			}
		}
	}

	/**
	 * Convert collection title to Shopify handle format
	 * Example: "2016 Chevrolet Spark" -> "2016-chevrolet-spark"
	 */
	function collectionTitleToHandle(title) {
		if (!title) return null;
		return title
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric with hyphens
			.replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
	}

	/**
	 * Normalize search text to handle "and" and "&" interchangeably
	 * Converts both to "&" for consistent matching
	 */
	function normalizeSearchText(text) {
		if (!text) return text;
		// Replace " and " with " & " (case-insensitive, with word boundaries)
		return text.replace(/\band\b/gi, "&");
	}

	function getStorefrontGraphQLUrl() {
		return `/api/${STOREFRONT_API_VERSION}/graphql.json`;
	}

	function parseCollectionTypes(value) {
		if (!value) return [];
		if (Array.isArray(value)) return value;

		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed : [];
		} catch (_) {
			return String(value)
				.split(",")
				.map((type) => type.trim())
				.filter(Boolean);
		}
	}

	function getProductSearchText(product) {
		const collectionTypes = Array.isArray(product.collection_types)
			? product.collection_types.join(" ")
			: product.collection_types || "";

		const parts = [
			product.title,
			product.product_type,
			collectionTypes,
		];

		return normalizeSearchText(
			parts.filter(Boolean).join(" ").toLowerCase(),
		);
	}

	function getProductSearchWords(product) {
		return getProductSearchText(product)
			.split(/[\s\/\-,;:]+/)
			.filter(Boolean);
	}

	/**
	 * Detect if a search query looks like a VIN (Vehicle Identification Number)
	 * VIN characteristics:
	 * - Exactly 17 characters
	 * - Only alphanumeric (no spaces, hyphens, or special chars)
	 * - Contains both letters and numbers
	 */
	function looksLikeVIN(query) {
		const trimmed = query.trim();

		// VINs are exactly 17 characters
		if (trimmed.length !== 17) {
			return false;
		}

		// VINs are alphanumeric only (no spaces or special characters)
		if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(trimmed)) {
			return false; // Invalid VIN pattern (excludes I, O, Q)
		}

		// Must contain both letters and numbers
		const hasLetter = /[A-HJ-NPR-Z]/i.test(trimmed);
		const hasNumber = /[0-9]/.test(trimmed);

		return hasLetter && hasNumber;
	}

	/**
	 * Detect if a search query looks like a SKU/part number
	 * SKU patterns:
	 * - OEM: 04715T3RA90ZZ (13 chars, alphanumeric)
	 * - Other: AC1000206 (9 chars, alphanumeric)
	 */
	function looksLikeSKU(query) {
		const trimmed = query.trim();

		// SKU characteristics:
		// 1. No spaces
		// 2. Only alphanumeric (and optional hyphens)
		// 3. Contains both letters AND numbers
		// 4. Length between 6-20 characters

		if (trimmed.length < 6 || trimmed.length > 20) {
			return false;
		}

		if (/\s/.test(trimmed)) {
			return false; // Contains spaces - not a SKU
		}

		if (!/^[A-Za-z0-9-]+$/.test(trimmed)) {
			return false; // Contains special characters other than hyphens
		}

		const hasLetter = /[A-Za-z]/.test(trimmed);
		const hasNumber = /[0-9]/.test(trimmed);

		if (!hasLetter || !hasNumber) {
			return false; // Must have both letters and numbers
		}

		// Passed all checks - looks like a SKU
		return true;
	}

	/**
	 * Detect values that could be OEM part numbers, including numeric-only
	 * values and values separated by a slash, period, or hyphen.
	 */
	function looksLikeOemPartNumber(query) {
		const trimmed = query.trim();
		const compact = trimmed.replace(/[./-]/g, "");

		return (
			compact.length >= 6 &&
			compact.length <= 80 &&
			/^[A-Za-z0-9]+(?:[./-][A-Za-z0-9]+)*$/.test(trimmed) &&
			/\d/.test(compact)
		);
	}

	function getOemPartNumberCandidates(query) {
		const uppercase = query.trim().toUpperCase();
		const compact = uppercase.replace(/[\s-]+/g, "");

		return Array.from(new Set([uppercase, compact])).filter(Boolean);
	}

	/**
	 * Find the most similar word/phrase to the query from matched products
	 * Handles both single and multi-word queries, including concatenated words
	 */
	function extractCorrectedWordFromMatches(query, fuseResults) {
		if (!fuseResults || fuseResults.length === 0) return null;

		const queryLower = query.toLowerCase().trim();
		let queryWords = queryLower.split(/[\s\/\-,;:]+/);

		// Collect all words from top 3 results - use Set for faster lookups and auto-deduplication
		const allWords = new Set();
		fuseResults.slice(0, 3).forEach((result) => {
			const title = result.item.title;
			const words = title.split(/[\s\/\-,;:]+/);

			words.forEach((word) => {
				// Clean the word
				const cleaned = word.toLowerCase().replace(/[^a-z]/g, "");
				// Skip short words and words with digits
				if (cleaned.length >= 3 && !/\d/.test(word)) {
					allWords.add(cleaned);
				}
			});
		});

		if (allWords.size === 0) return null;

		// If it's a MULTI-word query, check if combining the words makes a single valid word
		// Example: "tail gate" -> "tailgate"
		if (queryWords.length > 1) {
			const combinedWord = queryWords.join("");
			if (allWords.has(combinedWord)) {
				return combinedWord;
			}
		}

		// First try to find a single-word match before attempting to split
		let bestSingleWordMatch = null;
		let bestSingleWordScore = 0;

		if (queryWords.length === 1 && !allWords.has(queryLower)) {
			allWords.forEach((word) => {
				const queryCounts = {};
				const wordCounts = {};

				queryLower.split("").forEach((char) => {
					queryCounts[char] = (queryCounts[char] || 0) + 1;
				});

				word.split("").forEach((char) => {
					wordCounts[char] = (wordCounts[char] || 0) + 1;
				});

				let matchingChars = 0;
				Object.keys(queryCounts).forEach((char) => {
					if (wordCounts[char]) {
						matchingChars += Math.min(queryCounts[char], wordCounts[char]);
					}
				});

				const coverage = matchingChars / queryLower.length;
				const lengthDiff = Math.abs(word.length - queryLower.length);
				const lengthPenalty =
					lengthDiff / Math.max(word.length, queryLower.length);
				const score = coverage - lengthPenalty * 0.3;

				if (score > bestSingleWordScore) {
					bestSingleWordScore = score;
					bestSingleWordMatch = word;
				}
			});
		}

		// Only try splitting if:
		// 1. No good single-word match was found (score < 0.7)
		// 2. Query is long enough and looks concatenated
		if (
			queryWords.length === 1 &&
			queryLower.length >= 8 && // Increased from 6 to 8 - be more conservative
			!allWords.has(queryLower) &&
			bestSingleWordScore < 0.7 // Only split if no good single match
		) {
			const splitWords = trySplitConcatenatedWord(queryLower, allWords);
			if (splitWords && splitWords.length > 1) {
				queryWords = splitWords;
			}
		}

		// Process each word in the query
		const correctedWords = queryWords.map((queryWord) => {
			// If word is already correct (appears in products), keep it
			if (allWords.has(queryWord)) {
				return queryWord;
			}

			// If this is the original query and we found a good single-word match, use it
			if (
				queryWord === queryLower &&
				bestSingleWordMatch &&
				bestSingleWordScore > 0.66
			) {
				return bestSingleWordMatch;
			}

			// Find the best matching word using improved character frequency matching
			let bestWord = queryWord;
			let bestScore = 0;

			allWords.forEach((word) => {
				// Count character frequencies in both strings
				const queryCounts = {};
				const wordCounts = {};

				queryWord.split("").forEach((char) => {
					queryCounts[char] = (queryCounts[char] || 0) + 1;
				});

				word.split("").forEach((char) => {
					wordCounts[char] = (wordCounts[char] || 0) + 1;
				});

				// Count how many characters match (including frequency)
				let matchingChars = 0;
				Object.keys(queryCounts).forEach((char) => {
					if (wordCounts[char]) {
						matchingChars += Math.min(queryCounts[char], wordCounts[char]);
					}
				});

				// Score based on:
				// 1. How many query chars match (coverage)
				// 2. Length similarity (prefer similar length words)
				const coverage = matchingChars / queryWord.length;
				const lengthDiff = Math.abs(word.length - queryWord.length);
				const lengthPenalty =
					lengthDiff / Math.max(word.length, queryWord.length);

				// Combined score: high coverage, low length penalty
				const score = coverage - lengthPenalty * 0.3;

				if (score > bestScore) {
					bestScore = score;
					bestWord = word;
				}
			});

			// Only use correction if score is good enough (at least 66% coverage)
			if (bestScore > 0.66 && bestWord !== queryWord) {
				return bestWord;
			}

			return queryWord;
		});

		// Join corrected words back into phrase
		const correctedPhrase = correctedWords.join(" ");

		// Only return if something was actually corrected
		if (correctedPhrase !== queryLower) {
			return correctedPhrase;
		}

		return null;
	}

	/**
	 * Try to split a concatenated word into multiple words
	 * Example: "frontbumper" -> ["front", "bumper"]
	 */
	function trySplitConcatenatedWord(word, validWords) {
		const minWordLength = 3;
		let bestSplit = null;
		let bestScore = 0;

		// Try all possible split positions
		for (let i = minWordLength; i <= word.length - minWordLength; i++) {
			const part1 = word.substring(0, i);
			const part2 = word.substring(i);

			// Score how well each part matches valid words using character frequency
			let score1 = 0;
			let score2 = 0;
			let match1 = part1;
			let match2 = part2;

			// Find best match for first part using stricter character frequency matching
			validWords.forEach((validWord) => {
				const queryCounts = {};
				const wordCounts = {};

				part1.split("").forEach((char) => {
					queryCounts[char] = (queryCounts[char] || 0) + 1;
				});
				validWord.split("").forEach((char) => {
					wordCounts[char] = (wordCounts[char] || 0) + 1;
				});

				let matchingChars = 0;
				Object.keys(queryCounts).forEach((char) => {
					if (wordCounts[char]) {
						matchingChars += Math.min(queryCounts[char], wordCounts[char]);
					}
				});

				const coverage = matchingChars / part1.length;
				const lengthDiff = Math.abs(validWord.length - part1.length);
				const lengthPenalty =
					lengthDiff / Math.max(validWord.length, part1.length);
				const score = coverage - lengthPenalty * 0.4; // Higher penalty for length difference

				if (score > score1) {
					score1 = score;
					match1 = validWord;
				}
			});

			// Find best match for second part
			validWords.forEach((validWord) => {
				const queryCounts = {};
				const wordCounts = {};

				part2.split("").forEach((char) => {
					queryCounts[char] = (queryCounts[char] || 0) + 1;
				});
				validWord.split("").forEach((char) => {
					wordCounts[char] = (wordCounts[char] || 0) + 1;
				});

				let matchingChars = 0;
				Object.keys(queryCounts).forEach((char) => {
					if (wordCounts[char]) {
						matchingChars += Math.min(queryCounts[char], wordCounts[char]);
					}
				});

				const coverage = matchingChars / part2.length;
				const lengthDiff = Math.abs(validWord.length - part2.length);
				const lengthPenalty =
					lengthDiff / Math.max(validWord.length, part2.length);
				const score = coverage - lengthPenalty * 0.4;

				if (score > score2) {
					score2 = score;
					match2 = validWord;
				}
			});

			// Combined score - require BOTH parts to match well
			const avgScore = (score1 + score2) / 2;

			// Much stricter requirements:
			// 1. Both parts must score at least 0.75 (very good match)
			// 2. Different words
			// 3. Average score must be high
			if (
				avgScore > bestScore &&
				score1 > 0.75 &&
				score2 > 0.75 &&
				match1 !== match2
			) {
				bestScore = avgScore;
				bestSplit = [match1, match2];
			}
		}

		return bestSplit;
	}

	/**
	 * Filter search results to only show products that fit the selected vehicle
	 * This is the core fitment filtering logic
	 */
	function filterResultsByVehicleFitment() {
		// Use cached vehicle instead of calling getSelectedVehicleYMM
		const selectedVehicle = cachedSelectedVehicle;

		// If no vehicle selected, show notice
		if (!selectedVehicle) {
			showVehicleNotice();
			return;
		}

		// Find all product items with fitment collections
		const items = resultsContainer.querySelectorAll(
			"[data-fitment-collections]",
		);
		let visibleCount = 0;
		let removedCount = 0;

		items.forEach((item, index) => {
			const collectionsString = item.dataset.fitmentCollections;

			if (!collectionsString) {
				// No collections means universal fitment - keep it
				visibleCount++;
				return;
			}

			// Split collections by the separator used in Liquid
			const collections = collectionsString
				.split("||")
				.map((col) => col.trim());

			// Check if the selected vehicle is in the product's collections (case-insensitive)
			const hasMatch = collections.some(
				(col) => col.toLowerCase() === selectedVehicle.toLowerCase(),
			);

			if (!hasMatch) {
				// Vehicle doesn't match - remove this product
				item.remove();
				removedCount++;
			} else {
				visibleCount++;
			}
		});

		// If no products match, show "no results" message
		if (visibleCount === 0) {
			showNoFitmentResults();
		}
	}

	/**
	 * Show a prompt when user searches for a VIN instead of using the garage
	 */
	function showVINPrompt(vinQuery) {
		// Use self-hosted lottie system (lottie-web loaded globally via theme.liquid)
		resultsContainer.innerHTML = `
			<div class="predictive-search__vin-prompt">
				<div class="vin-prompt__icon">
					<div class="vin-hourglass" aria-hidden="true">
						<svg
							aria-label="loader being flipped clockwise and circled by three white curves fading in and out"
							role="img"
							height="56px"
							width="56px"
							viewBox="0 0 56 56"
							class="loader"
						>
							<clipPath id="sand-mound-top">
								<path
									d="M 14.613 13.087 C 15.814 12.059 19.3 8.039 20.3 6.539 C 21.5 4.789 21.5 2.039 21.5 2.039 L 3 2.039 C 3 2.039 3 4.789 4.2 6.539 C 5.2 8.039 8.686 12.059 9.887 13.087 C 11 14.039 12.25 14.039 12.25 14.039 C 12.25 14.039 13.5 14.039 14.613 13.087 Z"
									class="loader__sand-mound-top"
								></path>
							</clipPath>
							<clipPath id="sand-mound-bottom">
								<path
									d="M 14.613 20.452 C 15.814 21.48 19.3 25.5 20.3 27 C 21.5 28.75 21.5 31.5 21.5 31.5 L 3 31.5 C 3 31.5 3 28.75 4.2 27 C 5.2 25.5 8.686 21.48 9.887 20.452 C 11 19.5 12.25 19.5 12.25 19.5 C 12.25 19.5 13.5 19.5 14.613 20.452 Z"
									class="loader__sand-mound-bottom"
								></path>
							</clipPath>
							<g transform="translate(2,2)">
								<g
									transform="rotate(-90,26,26)"
									stroke-linecap="round"
									stroke-dashoffset="153.94"
									stroke-dasharray="153.94 153.94"
									stroke="hsl(0,0%,100%)"
									fill="none"
								>
									<circle
										transform="rotate(0,26,26)"
										r="24.5"
										cy="26"
										cx="26"
										stroke-width="2.5"
										class="loader__motion-thick"
									></circle>
									<circle
										transform="rotate(90,26,26)"
										r="24.5"
										cy="26"
										cx="26"
										stroke-width="1.75"
										class="loader__motion-medium"
									></circle>
									<circle
										transform="rotate(180,26,26)"
										r="24.5"
										cy="26"
										cx="26"
										stroke-width="1"
										class="loader__motion-thin"
									></circle>
								</g>
								<g transform="translate(13.75,9.25)" class="loader__model">
									<path
										d="M 1.5 2 L 23 2 C 23 2 22.5 8.5 19 12 C 16 15.5 13.5 13.5 13.5 16.75 C 13.5 20 16 18 19 21.5 C 22.5 25 23 31.5 23 31.5 L 1.5 31.5 C 1.5 31.5 2 25 5.5 21.5 C 8.5 18 11 20 11 16.75 C 11 13.5 8.5 15.5 5.5 12 C 2 8.5 1.5 2 1.5 2 Z"
										fill="hsl(var(--hue),90%,85%)"
									></path>

									<g stroke-linecap="round" stroke="hsl(35,90%,90%)">
										<line
											y2="20.75"
											x2="12"
											y1="15.75"
											x1="12"
											stroke-dasharray="0.25 33.75"
											stroke-width="1"
											class="loader__sand-grain-left"
										></line>
										<line
											y2="21.75"
											x2="12.5"
											y1="16.75"
											x1="12.5"
											stroke-dasharray="0.25 33.75"
											stroke-width="1"
											class="loader__sand-grain-right"
										></line>
										<line
											y2="31.5"
											x2="12.25"
											y1="18"
											x1="12.25"
											stroke-dasharray="0.5 107.5"
											stroke-width="1"
											class="loader__sand-drop"
										></line>
										<line
											y2="31.5"
											x2="12.25"
											y1="14.75"
											x1="12.25"
											stroke-dasharray="54 54"
											stroke-width="1.5"
											class="loader__sand-fill"
										></line>
										<line
											y2="31.5"
											x2="12"
											y1="16"
											x1="12"
											stroke-dasharray="1 107"
											stroke-width="1"
											stroke="hsl(35,90%,83%)"
											class="loader__sand-line-left"
										></line>
										<line
											y2="31.5"
											x2="12.5"
											y1="16"
											x1="12.5"
											stroke-dasharray="12 96"
											stroke-width="1"
											stroke="hsl(35,90%,83%)"
											class="loader__sand-line-right"
										></line>

										<g stroke-width="0" fill="hsl(35,90%,90%)">
											<path
												d="M 12.25 15 L 15.392 13.486 C 21.737 11.168 22.5 2 22.5 2 L 2 2.013 C 2 2.013 2.753 11.046 9.009 13.438 L 12.25 15 Z"
												clip-path="url(#sand-mound-top)"
											></path>
											<path
												d="M 12.25 18.5 L 15.392 20.014 C 21.737 22.332 22.5 31.5 22.5 31.5 L 2 31.487 C 2 31.487 2.753 22.454 9.009 20.062 Z"
												clip-path="url(#sand-mound-bottom)"
											></path>
										</g>
									</g>

									<g stroke-width="2" stroke-linecap="round" opacity="0.7" fill="none">
										<path
											d="M 19.437 3.421 C 19.437 3.421 19.671 6.454 17.914 8.846 C 16.157 11.238 14.5 11.5 14.5 11.5"
											stroke="hsl(0,0%,100%)"
											class="loader__glare-top"
										></path>
										<path
											transform="rotate(180,12.25,16.75)"
											d="M 19.437 3.421 C 19.437 3.421 19.671 6.454 17.914 8.846 C 16.157 11.238 14.5 11.5 14.5 11.5"
											stroke="hsla(0,0%,100%,0)"
											class="loader__glare-bottom"
										></path>
									</g>

									<rect height="2" width="24.5" fill="hsl(var(--hue),90%,50%)"></rect>
									<rect
										height="1"
										width="19.5"
										y="0.5"
										x="2.5"
										ry="0.5"
										rx="0.5"
										fill="hsl(var(--hue),90%,57.5%)"
									></rect>
									<rect
										height="2"
										width="24.5"
										y="31.5"
										fill="hsl(var(--hue),90%,50%)"
									></rect>
									<rect
										height="1"
										width="19.5"
										y="32"
										x="2.5"
										ry="0.5"
										rx="0.5"
										fill="hsl(var(--hue),90%,57.5%)"
									></rect>
								</g>
							</g>
						</svg>
					</div>
				</div>
				<h3 class="vin-prompt__title">VIN Search Detected</h3>
				<p class="vin-prompt__message">
					To search by VIN (<strong class="vin-prompt__vin-value"></strong>), please use the <strong>My Garage</strong> feature to decode your vehicle first.
				</p>
				<p class="vin-prompt__submessage">
					This will give you accurate, vehicle-specific parts that fit your exact make, model, and year.
				</p>
				<div class="vin-prompt__actions">
					<button type="button" class="vin-prompt__button vin-prompt__button--primary" onclick="
						document.getElementById('easyBtn')?.click();
					">
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M12 4v16m8-8H4"/>
						</svg>
						Add Vehicle to Garage
					</button>
				</div>
			</div>
		`;
		resultsContainer.style.display = "block";
		isOpen = true;

		// Safely inject VIN query via textContent (prevents XSS)
		const vinEl = resultsContainer.querySelector(".vin-prompt__vin-value");
		if (vinEl) vinEl.textContent = vinQuery;

		// Add custom styling for the VIN prompt
		if (!document.getElementById("vin-prompt-styles")) {
			const style = document.createElement("style");
			style.id = "vin-prompt-styles";
			style.textContent = `
				.predictive-search__vin-prompt {
					padding: 32px 24px;
					text-align: center;
					max-width: 500px;
					margin: 0 auto;
				}
				.vin-prompt__icon {
					padding-bottom: 16px;
					display: flex;
					justify-content: center;
					align-items: center;
					overflow: visible;
					line-height: 0;
					max-height: 180px;
				}
				.vin-prompt__icon .vin-hourglass {
					background: radial-gradient(circle, #1e293b 60%, #0f172a 100%);
					border-radius: 50%;
					width: 140px;
					height: 140px;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					box-shadow: 0 4px 16px rgba(15,23,42,0.18);
					overflow: hidden;
					font-size: 7px;
				}
				.vin-prompt__title {
					font-size: 20px;
					font-weight: 600;
					margin-bottom: 12px;
					color: #111827;
				}
				.vin-prompt__message {
					font-size: 15px;
					line-height: 1.6;
					color: #374151;
					margin-bottom: 8px;
				}
				.vin-prompt__submessage {
					font-size: 14px;
					line-height: 1.5;
					color: #6b7280;
					margin-bottom: 24px;
				}
				.vin-prompt__actions {
					display: flex;
					justify-content: center;
				}
				.vin-prompt__button {
					padding: 14px 32px;
					border-radius: 8px;
					font-size: 16px;
					font-weight: 500;
					cursor: pointer;
					transition: all 0.2s;
					border: none;
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 8px;
				}
				.vin-prompt__button--primary {
					background: #2563eb;
					color: white;
				}
				.vin-prompt__button--primary:hover {
					background: #1d4ed8;
					transform: translateY(-1px);
					box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
				}
			`;
			document.head.appendChild(style);
		}
	}

	function showVehicleNotice() {
		// if (vehicleNotice) {
		// 	vehicleNotice.style.display = "block";
		// }

		// Also show message in results area
		resultsContainer.innerHTML = `
      <div class="predictive-search__no-vehicle">
        <p>Select your vehicle to see parts that fit.</p>
        <button type="button" onclick="document.getElementById('easyBtn')?.click()">
          Select Vehicle
        </button>
      </div>
    `;
	}

	function insertVehicleReminderBanner() {
		// Only show when no vehicle is currently selected
		if (cachedSelectedVehicle) return;

		// Avoid inserting duplicate reminders on subsequent searches
		if (
			resultsContainer.querySelector(".predictive-search__vehicle-reminder")
		) {
			return;
		}

		const bannerHtml = `
			<div class="predictive-search__no-vehicle predictive-search__vehicle-reminder">
				<p>Select your vehicle to see parts that fit.</p>
				<button type="button" onclick="document.getElementById('easyBtn')?.click()">
					Select Vehicle
				</button>
			</div>
		`;

		resultsContainer.insertAdjacentHTML("afterbegin", bannerHtml);
	}

	function showNoFitmentResults() {
		const productsList = resultsContainer.querySelector(
			".search-flydown--product-items",
		);

		if (productsList) {
			productsList.innerHTML = `
        <div class="predictive-search__no-fitment">
          <p>No parts fit your selected vehicle.</p>
          <p class="text-small">Try a different search term or <a href="#" onclick="document.getElementById('easyBtn')?.click(); return false;">change your vehicle</a>.</p>
        </div>
      `;
		}
	}

	function showCollection() {
		resultsContainer.innerHTML = `
      <div class="predictive-search__no-results">
        <p>Your search did not match any products for your <b>${cachedSelectedVehicle}</b>.</br></br>Please try searching again using other keywords or browse all parts for your vehicle instead.</p>
        <button type="button" class="search-browse-catalog" onclick="
          const raw = localStorage.getItem('searchTerms');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed[0] && parsed[0].ymm) {
              const ymm = parsed[0].ymm.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
              window.location.href = '/collections/' + ymm;
            }
          }
        ">
          Browse Catalog
        </button>
      </div>
    `;
		resultsContainer.style.display = "block";
		isOpen = true;
	}

	function showLoading() {
		resultsContainer.innerHTML = `
      <div class="predictive-search__skeleton">
        <div class="skeleton-products">
          <div class="skeleton-products-title"></div>
          <div class="skeleton-product-item">
            <div class="skeleton-product-image"></div>
            <div class="skeleton-product-content">
              <div class="skeleton-product-title"></div>
              <div class="skeleton-product-vendor"></div>
              <div class="skeleton-product-price"></div>
            </div>
          </div>
          <div class="skeleton-product-item">
            <div class="skeleton-product-image"></div>
            <div class="skeleton-product-content">
              <div class="skeleton-product-title"></div>
              <div class="skeleton-product-vendor"></div>
              <div class="skeleton-product-price"></div>
            </div>
          </div>
          <div class="skeleton-product-item">
            <div class="skeleton-product-image"></div>
            <div class="skeleton-product-content">
              <div class="skeleton-product-title"></div>
              <div class="skeleton-product-vendor"></div>
              <div class="skeleton-product-price"></div>
            </div>
          </div>
        </div>
      </div>
    `;
		resultsContainer.style.display = "block";
		isOpen = true;
	}

	function showError() {
		resultsContainer.innerHTML = `
      <div class="predictive-search__error">
        <p>Search is temporarily unavailable. Please try again.</p>
      </div>
    `;
	}

	function open() {
		resultsContainer.style.display = "block";
		isOpen = true;

		if (closeButton) {
			closeButton.style.display = "block";
		}
	}

	function close(clearInput = true) {
		resultsContainer.style.display = "none";
		isOpen = false;

		if (closeButton) {
			closeButton.style.display = "none";
		}

		// Only clear the input field if explicitly requested
		if (clearInput) {
			input.value = "";
		}
	}

	/**
	 * Fetch search results from Shopify Predictive Search API
	 */
	async function getSearchResults(query) {
		if (query === currentQuery) {
			// Same query - just reopen the dropdown if it's closed
			if (!isOpen) {
				open();
			}
			return;
		}

		// New request id for this search
		const requestId = ++lastRequestId;

		// Mark this as the current query and clear any stale filtered collection types
		currentQuery = query;
		filteredCollectionTypes.clear();
		matchedProductType = null;
		matchedOemPartNumber = null;
		showLoading();

		try {
			// Check if query looks like a SKU - if so, search all products regardless of vehicle
			const isSKUSearch = looksLikeSKU(query);
			const isOemPartNumberSearch = looksLikeOemPartNumber(query);

			// Use cached vehicle instead of calling getSelectedVehicleYMM
			const selectedVehicle = cachedSelectedVehicle;

			if (
				isOemPartNumberSearch &&
				(await searchByOemPartNumber(query, requestId))
			) {
				// Exact OEM metafield matches are global and bypass vehicle filtering.
			} else if (isSKUSearch) {
				// SKU search - bypass vehicle filtering and search all products
				await searchAllProducts(query, requestId);
			} else if (selectedVehicle) {
				// Use collection-based search when vehicle is selected
				await searchInCollection(query, selectedVehicle, requestId);
			} else {
				// No vehicle - use standard predictive search
				await searchAllProducts(query, requestId);
			}

			// Before opening, verify the input still has this query
			// (user might have cleared it while we were searching)
			const currentInputValue = input.value.trim();
			if (
				currentInputValue.length === 0 ||
				currentInputValue.toLowerCase() !== query.toLowerCase()
			) {
				// Input was cleared or changed - don't show results
				if (currentInputValue.length === 0) {
					close();
				}
				return;
			}

			// Only open if this is still the latest request
			if (requestId === lastRequestId) {
				open();
			}
		} catch (error) {
			console.error("Predictive search error:", error);
			showError();
		}
	}

	/**
	 * Search all products using Shopify Predictive Search API
	 */
	async function searchAllProducts(query, requestId) {
		const searchUrl = `/search/suggest?q=${encodeURIComponent(
			query,
		)}&resources[type]=product&resources[limit]=500&section_id=predictive-search`;

		const response = await fetch(searchUrl);

		if (!response.ok) {
			throw new Error("Search request failed");
		}

		const htmlText = await response.text();
		// Only render results if this request is still the latest
		if (requestId === lastRequestId) {
			resultsContainer.innerHTML = htmlText;
			const images = resultsContainer.querySelectorAll("img");

			// Update total results count based on rendered product links
			const productLinks = resultsContainer.querySelectorAll(
				".search-flydown--product",
			);
			lastMatchingProductsCount = productLinks.length;

			images.forEach((img) => {
				const src = img.getAttribute("src");

				if (!src) return;

				// Kill Shopify lazy-loading completely
				img.removeAttribute("data-rimg");
				img.removeAttribute("data-rimg-template");
				img.removeAttribute("data-rimg-scale");
				img.removeAttribute("data-rimg-max");
				img.removeAttribute("data-rimg-crop");

				// THIS is the missing piece
				img.removeAttribute("srcset");
				img.removeAttribute("sizes");

				// Force the browser to re-evaluate the image
				img.loading = "eager";
				img.decoding = "async";
				img.src = ""; // clear first
				img.src = src; // then reassign
			});

			const items = resultsContainer.querySelectorAll(
				"[data-fitment-collections]",
			);

			if (items.length === 0) {
				showVehicleNotice();
			} else {
				insertVehicleReminderBanner();
			}
		} else {
			// Stale response - ignore
			console.debug("Ignored stale predictive HTML response for:", query);
		}
	}

	function mapStorefrontProductToSearchProduct(product) {
		const firstVariant = product.variants?.nodes?.[0] || {};
		const numericId = String(product.id || "").split("/").pop();
		const collectionTypes = parseCollectionTypes(
			product.collectionTypes?.value,
		);

		return {
			id: numericId,
			title: product.title || "",
			handle: product.handle || "",
			vendor: product.vendor || "",
			product_type: product.productType || "",
			collection_types: collectionTypes,
			images: product.featuredImage
				? [
						{
							src: product.featuredImage.url,
							alt: product.featuredImage.altText || product.title || "",
						},
					]
				: [],
			variants: [
				{
					available:
						product.availableForSale ??
						firstVariant.availableForSale ??
						false,
					price:
						product.availableForSale === true &&
						firstVariant.availableForSale === false
							? product.selectedOrFirstAvailableVariant?.price?.amount ??
								firstVariant.price?.amount ??
								""
							: firstVariant.price?.amount || "",
				},
			],
		};
	}

	async function fetchProductsByOemPartNumber(partNumber) {
		const query = `
			query oemPartNumberSearch($filters: [ProductFilter!]) {
				search(
					first: 50
					query: "*"
					types: [PRODUCT]
					productFilters: $filters
				) {
					totalCount
					nodes {
						... on Product {
							id
							title
							handle
							productType
							vendor
							availableForSale
							selectedOrFirstAvailableVariant {
								price {
									amount
								}
							}
							featuredImage {
								url
								altText
							}
							variants(first: 1) {
								nodes {
									availableForSale
									price {
										amount
									}
								}
							}
						}
					}
				}
			}
		`;

		const response = await fetch(getStorefrontGraphQLUrl(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				query,
				variables: {
					filters: [
						{
							productMetafield: {
								namespace: OEM_PART_NUMBER_METAFIELD.namespace,
								key: OEM_PART_NUMBER_METAFIELD.key,
								value: partNumber,
							},
						},
					],
				},
			}),
		});

		if (!response.ok) {
			throw new Error(`OEM search request failed: ${response.status}`);
		}

		const result = await response.json();
		if (result.errors?.length) {
			throw new Error(result.errors.map((error) => error.message).join(", "));
		}

		return result.data?.search || { nodes: [], totalCount: 0 };
	}

	async function searchByOemPartNumber(query, requestId) {
		try {
			for (const candidate of getOemPartNumberCandidates(query)) {
				const searchResult = await fetchProductsByOemPartNumber(candidate);

				if (requestId !== lastRequestId) return false;

				const products = (searchResult.nodes || [])
					.filter((product) => product && product.handle)
					.map(mapStorefrontProductToSearchProduct);

				if (products.length === 0) continue;

				matchedOemPartNumber = candidate;
				lastMatchingProductsCount =
					searchResult.totalCount || products.length;
				renderProductResults(products, cachedSelectedVehicle || "", null, query);
				return true;
			}
		} catch (error) {
			console.warn("OEM part number search failed; using standard search:", error);
		}

		return false;
	}

	function mergeProductsByHandle(collectionProducts, universalProducts) {
		const merged = [];
		const seen = new Set();

		[...(collectionProducts || []), ...(universalProducts || [])].forEach(
			(product) => {
				const key = product.handle || String(product.id || "");
				if (!key || seen.has(key)) return;

				seen.add(key);
				merged.push(product);
			},
		);

		return merged;
	}

	async function fetchUniversalProductsForCollection(collectionHandle) {
		if (!collectionHandle) return [];

		if (universalProductsByCollectionHandle.has(collectionHandle)) {
			return universalProductsByCollectionHandle.get(collectionHandle);
		}

		if (!STOREFRONT_ACCESS_TOKEN) {
			universalProductsByCollectionHandle.set(collectionHandle, []);
			return [];
		}

		// Collection pages inject these non-member products from the vehicle lookup;
		// include them so fitment-aware search matches the collection grid.
		const query = `
			query vehicleUniversalProducts($handle: String!) {
				metaobject(handle: { type: "year_make_model_lookup", handle: $handle }) {
					universalProducts: field(key: "universal_products") {
						references(first: 50) {
							nodes {
								... on Product {
									id
									title
									handle
									productType
									vendor
									availableForSale
									featuredImage {
										url
										altText
									}
									variants(first: 1) {
										nodes {
											availableForSale
											price {
												amount
											}
										}
									}
									collectionTypes: metafield(namespace: "custom", key: "collection_types") {
										value
									}
								}
							}
						}
					}
				}
			}
		`;

		try {
			const response = await fetch(getStorefrontGraphQLUrl(), {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
				},
				body: JSON.stringify({
					query,
					variables: {
						handle: collectionHandle,
					},
				}),
			});

			if (!response.ok) {
				universalProductsByCollectionHandle.set(collectionHandle, []);
				return [];
			}

			const result = await response.json();
			const nodes =
				result.data?.metaobject?.universalProducts?.references?.nodes || [];
			const universalProducts = nodes
				.filter((node) => node && node.handle)
				.map(mapStorefrontProductToSearchProduct);

			universalProductsByCollectionHandle.set(
				collectionHandle,
				universalProducts,
			);
			return universalProducts;
		} catch (error) {
			universalProductsByCollectionHandle.set(collectionHandle, []);
			return [];
		}
	}

	/**
	 * Search within a specific vehicle collection
	 */
	async function searchInCollection(query, vehicleCollection, requestId) {
		// Use cached collection handle instead of converting every time
		const collectionHandle = cachedCollectionHandle;

		// Try using the collection's product list endpoint with search filter
		// Format: /collections/{handle}/products.json
		const searchUrl = `/collections/${collectionHandle}/products.json?limit=500&q=${encodeURIComponent(
			query,
		)}`;

		const response = await fetch(searchUrl);

		if (!response.ok) {
			console.warn(
				"Collection search failed, falling back to predictive search",
			);
			// Fallback to predictive search with client-side filtering
			await searchAllProducts(query);
			filterResultsByVehicleFitment();
			return;
		}

		const data = await response.json();
		const products = mergeProductsByHandle(
			data.products || [],
			await fetchUniversalProductsForCollection(collectionHandle),
		);

		// Filter products by search query (case-insensitive)
		// Normalize to handle "and" and "&" interchangeably
		const lowerQuery = normalizeSearchText(query.toLowerCase().trim());

		// First, check if the query appears exactly in searchable product text.
		const hasExactMatch = products.some((product) => {
			const searchText = getProductSearchText(product);
			const searchWords = getProductSearchWords(product);
			const queryWords = lowerQuery.split(/[\s\/\-,;:]+/);

			return queryWords.every((queryWord) =>
				searchWords.includes(queryWord),
			) || searchText.includes(lowerQuery);
		});

		let matchingProducts;
		let results = [];

		if (hasExactMatch) {
			// Use word-by-word matching (order-independent)
			const queryWords = lowerQuery.split(/[\s\/\-,;:]+/);

			matchingProducts = products.filter((product) => {
				const searchText = getProductSearchText(product);
				const searchWords = getProductSearchWords(product);

				// Check if all query words appear in searchable product text.
				const allWordsMatch = queryWords.every((queryWord) =>
					searchWords.includes(queryWord),
				);

				// Also check for exact substring match for phrases
				const textMatch = searchText.includes(lowerQuery);

				return allWordsMatch || textMatch;
			});
		} else {
			// Use fuzzy matching with Fuse.js for typos
			let threshold = 0.3; // default for medium-length words

			if (lowerQuery.length <= 3) {
				threshold = 0.4; // very forgiving for short words
			} else if (lowerQuery.length === 4) {
				threshold = 0.35;
			} else if (lowerQuery.length >= 5 && lowerQuery.length <= 7) {
				threshold = 0.3; // allow ~2 char difference for 5-7 char words
			} else {
				threshold = 0.25; // slightly stricter for very long words
			}

			// Reuse Fuse instance or create on first use
			if (!fuseInstance) {
				fuseInstance = new Fuse([], {
					keys: [
						{ name: "title", weight: 0.8 },
						{ name: "product_type", weight: 0.2 },
						{ name: "collection_types", weight: 0.1 },
					],
					threshold: threshold,
					ignoreLocation: true,
					minMatchCharLength: 2,
				});
			}

			// Update the collection and threshold
			fuseInstance.setCollection(products);
			fuseInstance.options.threshold = threshold;

			// Normalize query for fuzzy search
			results = fuseInstance.search(normalizeSearchText(query));

			matchingProducts = results.map((r) => r.item);
		}

		if (matchingProducts.length === 0) {
			lastMatchingProductsCount = 0;
			showCollection();
			return;
		}

		// Track matching products count for logging
		lastMatchingProductsCount = matchingProducts.length;

		// Extract the corrected word - need to check all products for suggestions, not just fuzzy results
		// For exact matches, results will be empty, so we create fake results from matchingProducts
		const resultsForCorrection =
			results.length > 0 ? results : matchingProducts.map((p) => ({ item: p }));

		const correctedWord = extractCorrectedWordFromMatches(
			query,
			resultsForCorrection,
		);
		const didYouMeanSuggestion =
			correctedWord && correctedWord !== query.toLowerCase()
				? correctedWord
				: null;

		if (matchingProducts && matchingProducts.length > 0) {
			// Capture the query for this request so we don't overwrite types for
			// a newer search that may have started while this one was in flight.
			const thisQuery = query;

			// Check if query matches a specific product type FIRST
			const productTypeMatch = findMatchingProductType(
				matchingProducts,
				query,
				didYouMeanSuggestion,
			);

			// If we have a product type match, only extract collection types from products with that type
			// Otherwise, extract from all matching products
			const productsForTypeExtraction = productTypeMatch
				? matchingProducts.filter((p) => p.product_type === productTypeMatch)
				: matchingProducts;

			const types = await fetchCollectionTypesViaGraphQL(
				productsForTypeExtraction,
			);

			// Assign types only if this response is still current
			if (requestId === lastRequestId && thisQuery === currentQuery) {
				filteredCollectionTypes = types;
				matchedProductType = productTypeMatch;
			} else {
				console.debug("Ignored stale collection types for:", thisQuery);
			}
		}

		// Render products manually since we're using JSON API - only if latest
		if (requestId === lastRequestId) {
			renderProductResults(
				matchingProducts,
				vehicleCollection,
				filteredCollectionTypes.size === 1 ? didYouMeanSuggestion : null,
				query,
			);
		} else {
			console.debug("Ignored stale product render for:", query);
		}
	}

	/**
	 * Render product results from JSON data
	 */
	function renderProductResults(
		products,
		vehicleTitle,
		didYouMean = null,
		originalQuery = "",
	) {
		if (products.length === 0) {
			showNoFitmentResults();
			return;
		}

		const suggestionHTML = didYouMean
			? `
		<div class="predictive-search__suggestion">
			Did you mean 
			<button type="button" data-suggestion="${didYouMean}">
				${didYouMean}
			</button>?
		</div>
	`
			: "";

		const productListHTML = products
			.slice(0, 10) // Only render first 10 products for performance
			.sort((a, b) => {
				// Sort by availability: in-stock first, out-of-stock last
				const aAvailable = a.variants[0]?.available ?? false;
				const bAvailable = b.variants[0]?.available ?? false;
				return bAvailable - aAvailable; // true (1) - false (0) = 1, so in-stock comes first
			})
			.map((product) => {
				const image = product.images[0];
				const price = product.variants[0]?.price;
				const url = `/products/${product.handle}`;
				const productAvailable = product.variants[0]?.available;

				// Generate thumbnail URL (50x50px) for faster loading
				const thumbnailSrc = image?.src
					? image.src.replace(/\.(jpg|jpeg|png|webp)/i, "_50x50.$1")
					: null;

				return `
				<li data-fitment-collections="${vehicleTitle}">
					<a class="search-flydown--product" href="${url}">
						<figure class="search-flydown--product-image">
							${
								thumbnailSrc
									? `<img src="${thumbnailSrc}" alt="${
											image.alt || product.title
										}" loading="lazy" width="50" height="50">`
									: ""
							}
						</figure>
						<div class="search-flydown--product-text">
							<span class="search-flydown--product-title">${product.title}</span>
							${
								product.vendor
									? `<span class="search-flydown--product-vendor">${product.vendor}</span>`
									: ""
							}
							${price ? `<div class="search-flydown--product-price">$${price}</div>` : ""}
							<div class="search-flydown--product-stock" style="color: ${
								productAvailable ? "#22c55e" : "#ef4444"
							}; font-size: 12px; margin-top: 4px;">
								${productAvailable ? "In stock" : "Out of stock"}
							</div>
						</div>
					</a>
				</li>
			`;
			})
			.join("");

		// Build a "view all results" button similar to the Liquid template,
		// but only when there are more products than we show in the dropdown.
		let continueButtonHTML = "";
		if (products.length > 10) {
			const trimmedQuery = (originalQuery || "").trim();

			if (trimmedQuery.length > 0) {
				// Basic HTML escaping for safety
				const escapedQuery = trimmedQuery
					.replace(/&/g, "&amp;")
					.replace(/</g, "&lt;")
					.replace(/>/g, "&gt;");

				continueButtonHTML = `
					<button class="search-flydown__continue" type="submit">
						View all results for "${escapedQuery}"
					</button>
				`;
			} else {
				continueButtonHTML = `
					<button class="search-flydown__continue" type="submit">
						View all results
					</button>
				`;
			}
		}

		resultsContainer.innerHTML = `
			${suggestionHTML}
			<div class="search-flydown--product-items">
				<h3 class="search-flydown__group-title">Products</h3>
				<ul class="search-flydown__group-list">
					${productListHTML}
				</ul>
			</div>
			${continueButtonHTML}
		`;
	}

	function handleInput(e) {
		// Clear previous timer - this stops any pending search
		clearTimeout(debounceTimer);

		// Get current query value
		const query = e.target.value.trim();

		// Create/refresh pending input promise so submit can wait for completion
		if (pendingInputResolve) {
			// Resolve any previous pending promise to avoid leaks (we only care about the latest)
			pendingInputResolve();
		}

		pendingInputPromise = new Promise((resolve, reject) => {
			pendingInputResolve = resolve;
			pendingInputReject = reject;
		});

		// Close if query is empty
		if (query.length === 0) {
			close();
			// Resolve pending immediately
			if (pendingInputResolve) {
				pendingInputResolve();
				pendingInputPromise = null;
				pendingInputResolve = null;
				pendingInputReject = null;
			}
			return;
		}

		// Check if query looks like a VIN (17 characters)
		if (query.length === 17 && looksLikeVIN(query)) {
			// VIN detected - show prompt immediately
			showVINPrompt(query);
			if (pendingInputResolve) {
				pendingInputResolve();
				pendingInputPromise = null;
				pendingInputResolve = null;
				pendingInputReject = null;
			}
			return;
		}

		// Wait for user to stop typing (300ms debounce)
		debounceTimer = setTimeout(async () => {
			// Double-check the input value hasn't changed during the debounce delay
			const currentQuery = input.value.trim();

			if (currentQuery.length === 0) {
				// Input was cleared during debounce - don't search
				if (pendingInputResolve) {
					pendingInputResolve();
					pendingInputPromise = null;
					pendingInputResolve = null;
					pendingInputReject = null;
				}
				return;
			}

			try {
				await getSearchResults(currentQuery);
			} catch (err) {
				// ignore errors here; getSearchResults already logs
			} finally {
				if (pendingInputResolve) {
					pendingInputResolve();
					pendingInputPromise = null;
					pendingInputResolve = null;
					pendingInputReject = null;
				}
			}
		}, 400);
	}

	function handleFocus() {
		const query = input.value.trim();
		if (query.length > 0) {
			getSearchResults(query);
		}
	}

	/**
	 * Fetch collection types for products using Shopify GraphQL API
	 */
	async function fetchCollectionTypesViaGraphQL(products) {
		try {
			if (!STOREFRONT_ACCESS_TOKEN) {
				console.warn("Storefront API access token not configured");
				return [];
			}

			// Build array of product GIDs
			const productIds = products.map(
				(product) => `gid://shopify/Product/${product.id}`,
			);

			// GraphQL query for Storefront API
			const query = `
				query productTypes($ids: [ID!]!) {
					nodes(ids: $ids) {
						... on Product {
							id
							metafield(namespace: "custom", key: "collection_types") {
								value
							}
						}
					}
				}
			`;

			// Make GraphQL request to Storefront API
			const requestBody = {
				query: query,
				variables: {
					ids: productIds,
				},
			};

			const response = await fetch(
				getStorefrontGraphQLUrl(),
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
					},
					body: JSON.stringify(requestBody),
				},
			);

			if (!response.ok) {
				console.warn("GraphQL request failed:", response.status);
				const errorText = await response.text();
				console.warn("Error details:", errorText);
				return [];
			}

			const result = await response.json();

			// Extract collection types from the response - use Set for auto-deduplication
			const collectionTypes = new Set();

			if (result.data && result.data.nodes) {
				result.data.nodes.forEach((node) => {
					if (node && node.metafield && node.metafield.value) {
						try {
							// Parse the JSON array string
							const types = JSON.parse(node.metafield.value);
							if (Array.isArray(types)) {
								types.forEach((type) => collectionTypes.add(type));
							}
						} catch (e) {
							console.warn("Failed to parse metafield value:", e);
						}
					}
				});
			}

			return collectionTypes;
		} catch (error) {
			console.error("Error fetching collection types via GraphQL:", error);
			return [];
		}
	}

	/**
	 * Find matching product type from products based on query or corrected suggestion
	 * Optimized: Build type index first for O(1) lookup instead of O(n) iteration
	 */
	function findMatchingProductType(products, query, correctedQuery) {
		if (!products || products.length === 0) return null;

		// Use corrected query if available, otherwise use original query
		const searchTerm = (correctedQuery || query).toLowerCase().trim();

		// Build a type index: Map of lowercase type -> {originalType, count}
		const typeIndex = new Map();
		products.forEach((product) => {
			if (product.product_type) {
				const typeLower = product.product_type.toLowerCase();
				if (!typeIndex.has(typeLower)) {
					typeIndex.set(typeLower, {
						originalType: product.product_type,
						count: 0,
					});
				}
				typeIndex.get(typeLower).count++;
			}
		});

		// O(1) lookup instead of O(n) iteration
		const match = typeIndex.get(searchTerm);
		if (match) {
			return match.originalType;
		}

		return null;
	}

	/**
	 * Build URL with collection type filters and product type filter
	 */
	function buildFilteredUrl(collectionHandle, query) {
		let url = `/collections/${collectionHandle}`;
		const params = [];

		// Add filter parameters for each matching collection type
		if (filteredCollectionTypes.size > 0 && filteredCollectionTypes.size <= 4) {
			const typeParams = Array.from(filteredCollectionTypes).map(
				(type) =>
					`filter.p.m.custom.collection_types=${encodeURIComponent(type)}`,
			);
			params.push(...typeParams);
		}

		// Add product type filter if we found a matching type
		if (matchedProductType) {
			// Format: "Passenger Side Fender" -> "Passenger+Side+Fender"
			const formattedType = matchedProductType.split(" ").join("+");
			params.push(`filter.p.product_type=${formattedType}`);
		}

		if (params.length > 0) {
			url += `?${params.join("&")}`;
		}

		return url;
	}

	function buildOemSearchUrl(partNumber) {
		const params = new URLSearchParams();
		params.set("q", "*");
		params.set(
			`filter.p.m.${OEM_PART_NUMBER_METAFIELD.namespace}.${OEM_PART_NUMBER_METAFIELD.key}`,
			partNumber,
		);
		return `/search?${params.toString()}`;
	}

	/**
	 * Log search query data to Google Sheets
	 * Uses sendBeacon for guaranteed delivery during page navigation
	 */
	function logToGoogleSheets(payload) {
		const endpoint =
			"https://script.google.com/macros/s/AKfycbx6glxK-dcDZlLX_-Rp74II6dXhIF9MElXSA-AMLz2UjfdUjsMtTfvFwDMhaneRbqaskw/exec";

		if (navigator.sendBeacon) {
			// sendBeacon is the best option - browser guarantees delivery
			const blob = new Blob([JSON.stringify(payload)], {
				type: "text/plain;charset=UTF-8",
			});
			navigator.sendBeacon(endpoint, blob);
		} else {
			// Fallback for browsers without sendBeacon
			fetch(endpoint, {
				redirect: "follow",
				method: "POST",
				headers: {
					"Content-Type": "text/plain;charset=UTF-8",
				},
				body: JSON.stringify(payload),
				keepalive: true, // Allows request to continue after page unload
			}).catch((err) => {
				console.error("Failed to log to sheets:", err);
			});
		}
	}

	async function handleSubmit(e) {
		e.preventDefault(); // Prevent default form submission
		e.stopPropagation(); // Stop other handlers from running
		e.stopImmediatePropagation(); // Stop ALL other handlers

		const query = input.value.trim();
		const selectedVehicle = getSelectedVehicleYMM(true, false);

		if (!query) {
			return; // Don't submit empty search
		}

		// Check if query looks like a VIN
		if (looksLikeVIN(query)) {
			// VIN detected - show prompt instead of searching
			showVINPrompt(query);
			return false; // Stop form submission
		}

		let error = null;
		let targetUrl = null;
		let indVehicle = null;

		try {
			// If input handling is in progress, wait for it (with a short timeout fallback)
			if (pendingInputPromise) {
				try {
					await pendingInputPromise;
				} catch (err) {
					// ignore
				}
			} else {
				// If there's no pending promise, the final `input` event may not have fired
				// (user pressed Enter quickly). Trigger a synchronous search and await it
				// so submission uses the latest processed results.
				try {
					await getSearchResults(query);
				} catch (err) {
					// ignore errors and continue with submission
					error = err.message || String(err);
				}
			}

			if (matchedOemPartNumber) {
				targetUrl = buildOemSearchUrl(matchedOemPartNumber);
			} else if (looksLikeSKU(query)) {
				// SKU searches bypass vehicle filtering in the dropdown and on submit.
				targetUrl = `/search?q=${encodeURIComponent(query)}`;
			} else if (selectedVehicle) {
				// Use cached collection handle instead of converting every time
				targetUrl = buildFilteredUrl(cachedCollectionHandle, query);
			} else {
				targetUrl = `/search?q=${encodeURIComponent(query)}`;
			}

			indVehicle = getSelectedVehicleYMM(false, true);
		} catch (err) {
			// Catch any unexpected errors during form submission
			error = err.message || String(err);
			// Fallback to basic search URL
			targetUrl = `/search?q=${encodeURIComponent(query)}`;
		} finally {
			// Get individual vehicle details if not already fetched
			if (!indVehicle) {
				indVehicle = getSelectedVehicleYMM(false, true);
			}

			window.igEvents = window.igEvents || [];
			window.igEvents.push({
				event: "searchSubmitted",
			});

			// Log to Google Sheets
			const payload = {
				query: query,
				totalResults: lastMatchingProductsCount,
				year: indVehicle?.year || null,
				make: indVehicle?.make || null,
				model: indVehicle?.model || null,
				submodel: indVehicle?.submodel || null,
				engine: indVehicle?.engine || null,
				selectedProduct: null,
				error: error,
				timestamp: new Date().toISOString(),
			};

			logToGoogleSheets(payload);

			// Navigate to target URL
			if (targetUrl) {
				window.location.href = targetUrl;
			}
		}

		return false; // Extra insurance
	}

	function init() {
		// Initialize cached vehicle and collection handle
		cachedSelectedVehicle = getSelectedVehicleYMM(true, false);
		cachedCollectionHandle = collectionTitleToHandle(cachedSelectedVehicle);

		// Listen for searchTerms updates from global-library.js
		window.addEventListener("searchTermsUpdated", () => {
			cachedSelectedVehicle = getSelectedVehicleYMM(true, false);
			cachedCollectionHandle = collectionTitleToHandle(cachedSelectedVehicle);
		});

		// Bind event listeners
		input.addEventListener("input", handleInput);
		input.addEventListener("focus", handleFocus);
		form.addEventListener("submit", handleSubmit);

		if (closeButton) {
			closeButton.addEventListener("click", close);
		}

		// Close on outside click
		document.addEventListener("click", (e) => {
			// Don't close if clicking inside the form/results or if the input is focused
			if (
				!form.contains(e.target) &&
				!resultsContainer.contains(e.target) &&
				document.activeElement !== input
			) {
				close(false); // Don't clear input on outside click
			}
		});

		// Close on escape key
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && isOpen) {
				close(false); // Don't clear input on escape
			}
		});

		// Handle clicks on search results (suggestions and products)
		resultsContainer.addEventListener("click", (e) => {
			// Handle "Did you mean" suggestion clicks
			const btn = e.target.closest("[data-suggestion]");
			if (btn) {
				// Update input value with suggestion
				input.value = btn.dataset.suggestion;
				// Submit the form
				form.requestSubmit();
				return;
			}

			// Handle product clicks - log to Google Sheets
			const productLink = e.target.closest(".search-flydown--product");
			if (productLink) {
				// Get the product title
				const productTitle =
					productLink.querySelector(".search-flydown--product-title")
						?.textContent || "";
				const query = input.value.trim();
				const indVehicle = getSelectedVehicleYMM(false, true);

				// Log to Google Sheets
				const payload = {
					query: query,
					totalResults: lastMatchingProductsCount,
					year: indVehicle?.year || null,
					make: indVehicle?.make || null,
					model: indVehicle?.model || null,
					submodel: indVehicle?.submodel || null,
					engine: indVehicle?.engine || null,
					selectedProduct: productTitle,
					error: null,
					timestamp: new Date().toISOString(),
				};

				logToGoogleSheets(payload);

				// Let the default link navigation happen
			}
		});
	}

	// Initialize
	init();

	// Return public API
	return {
		open,
		close,
		getSelectedVehicleYMM,
	};
}

// Auto-initialize when DOM is ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		window.predictiveSearch = createPredictiveSearch();
	});
} else {
	window.predictiveSearch = createPredictiveSearch();
}

// Export factory function for manual initialization if needed
window.createPredictiveSearch = createPredictiveSearch;
